import { format, parseISO } from "date-fns";
import { Copy, RotateCcw, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { StatusIcon } from "@/features/attorney/views/Entries/StatusIcon";
import type { EntryStatus } from "@/shared/cache";
import type { Matter } from "@/shared/cache/slices/matters";
import type { Entry } from "@/shared/types/entries";
import {
	canModifyExportedEntry,
	isTysonMendesProLawCutoffLocked,
	PROLAW_CUTOFF_LOCK_MESSAGE,
} from "@/shared/utils/exportedEntryMutation";
import { AttachmentField } from "@/v2/components/entry-form/AttachmentField";
import {
	EntryForm,
	type EntryFormHandle,
} from "@/v2/components/entry-form/EntryForm";
import type { TimeEntryFormValues } from "@/v2/components/entry-form/schema";
import { Banner } from "@/v2/components/ui/banner";
import {
	ResponsiveDialog,
	ResponsiveDialogContent,
} from "@/v2/components/ui/responsive-dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
} from "@/v2/components/ui/select";
import { TooltipButton } from "@/v2/components/ui/tooltip-button";
import { createEntryFormDefaults } from "@/v2/lib/entryPayloads";

export interface EditEntryDialogProps {
	entry: Entry | null;
	failedExports: ReadonlyMap<string, string>;
	status?: EntryStatus;
	onOpenChange: (open: boolean) => void;
	onResolveFeedback: (entryId: string) => void;
	onSubmit: (
		entry: Entry,
		values: TimeEntryFormValues,
		status?: EntryStatus,
	) => void;
	onExport: (entry: Entry, values: TimeEntryFormValues) => void;
	onDuplicate?: (entry: Entry, values: TimeEntryFormValues) => void;
	onDelete?: (entry: Entry) => void;
	onRestore?: (entry: Entry) => void;
	canUploadFiles: boolean;
}

/**
 * Build a minimal Matter from the entry's own denormalized snapshot so the
 * matter picker renders the selected value immediately, instead of sitting blank
 * until the full matters list loads over the network.
 */
function selectedMatterFromEntry(entry: Entry): Matter | null {
	if (!entry.matter?.id) return null;
	return {
		matter_id: entry.matter.id,
		matter: entry.matter.code,
		client: entry.matter.client ?? "",
		description: entry.matter.description ?? "",
		task_group: null,
		activity_group: null,
		billing_category: "unknown",
		billable_category: "unknown",
	};
}

const STATUS_OPTIONS: ReadonlyArray<{ value: EntryStatus; label: string }> = [
	{ value: "tracked", label: "Tracked" },
	{ value: "reviewed", label: "Reviewed" },
];

const STATUS_LABEL: Record<EntryStatus, string> = {
	tracked: "Tracked",
	reviewed: "Reviewed",
	exported: "Exported",
	deleted: "Deleted",
};

function StatusOption({ status }: { status: EntryStatus }) {
	return (
		<div className="flex items-center gap-2">
			<StatusIcon status={status} className="size-3.5" />
			<span>{STATUS_LABEL[status]}</span>
		</div>
	);
}

interface StatusSelectProps {
	status: EntryStatus | undefined;
	currentStatus: EntryStatus | undefined;
	onStatusChange: (status: EntryStatus) => void;
	exportedAt?: string | null;
}

function StatusSelect({
	status,
	currentStatus,
	onStatusChange,
	exportedAt,
}: StatusSelectProps) {
	if (!status) return null;

	const isTerminalStatus = status === "exported";

	return (
		<Select
			value={currentStatus}
			onValueChange={(value) => onStatusChange(value as EntryStatus)}
			disabled={isTerminalStatus}
		>
			<SelectTrigger
				className={
					exportedAt
						? "h-9 w-auto min-w-[126px] bg-card"
						: "h-9 w-[126px] bg-card"
				}
			>
				{currentStatus && exportedAt ? (
					<div className="flex items-center gap-2 whitespace-nowrap">
						<StatusIcon status={currentStatus} className="size-3.5" />
						<span>{STATUS_LABEL[currentStatus]}</span>
						<span className="font-normal text-muted-foreground tabular-nums">
							{exportedAt}
						</span>
					</div>
				) : currentStatus ? (
					<StatusOption status={currentStatus} />
				) : (
					<span className="text-muted-foreground">Status</span>
				)}
			</SelectTrigger>
			<SelectContent>
				{!STATUS_OPTIONS.some((o) => o.value === status) && (
					<SelectItem value={status} disabled>
						<StatusOption status={status} />
					</SelectItem>
				)}
				{STATUS_OPTIONS.map((option) => (
					<SelectItem key={option.value} value={option.value}>
						<StatusOption status={option.value} />
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
}

export function EditEntryDialog({
	entry,
	failedExports,
	status,
	onOpenChange,
	onResolveFeedback,
	onSubmit,
	onExport,
	onDuplicate,
	onDelete,
	onRestore,
	canUploadFiles,
}: EditEntryDialogProps) {
	const formRef = useRef<EntryFormHandle>(null);
	const [selectedStatus, setSelectedStatus] = useState<EntryStatus | undefined>(
		status,
	);

	useEffect(() => {
		setSelectedStatus(status);
	}, [status]);

	const isDeleted = status === "deleted";
	const isExported = status === "exported";
	const canMutateExported = entry ? canModifyExportedEntry(entry) : false;
	const fieldsReadOnly = isDeleted || (isExported && !canMutateExported);
	const exportedAt = entry?.entryExportedAt
		? format(parseISO(entry.entryExportedAt), "MMM d, yyyy 'at' h:mm a")
		: null;

	return (
		<ResponsiveDialog open={entry !== null} onOpenChange={onOpenChange}>
			<ResponsiveDialogContent
				className="!max-h-[90vh]"
				title="Edit Entry"
				headerClassName="p-4 pb-2"
				footer={
					<div className="flex items-center justify-between gap-3">
						<div className="flex items-center gap-2">
							{entry &&
							!isDeleted &&
							(!isExported || canMutateExported) &&
							onDelete ? (
								<TooltipButton
									type="button"
									variant="outline"
									size="icon"
									className="border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
									aria-label="Delete entry"
									tooltip="Delete entry"
									onClick={() => onDelete(entry)}
								>
									<Trash2 />
								</TooltipButton>
							) : null}
							{entry && !isDeleted && onDuplicate && (
								<TooltipButton
									type="button"
									variant="outline"
									size="icon"
									aria-label="Duplicate entry"
									tooltip="Duplicate entry"
									onClick={() => formRef.current?.submitDuplicate()}
								>
									<Copy />
								</TooltipButton>
							)}
						</div>
						<div className="flex items-center gap-2">
							{isDeleted ? (
								entry && onRestore ? (
									<TooltipButton
										type="button"
										size="sm"
										aria-label="Restore entry"
										tooltip="Restore entry"
										onClick={() => onRestore(entry)}
									>
										<RotateCcw />
										Restore
									</TooltipButton>
								) : null
							) : (
								<>
									<StatusSelect
										status={status}
										currentStatus={selectedStatus}
										onStatusChange={setSelectedStatus}
										exportedAt={exportedAt}
									/>
									{!isExported && (
										<TooltipButton
											variant="outline"
											size="sm"
											tooltip="Export"
											onClick={() => formRef.current?.submitExport()}
										>
											Export
										</TooltipButton>
									)}
									{(!isExported || canMutateExported) && (
										<TooltipButton
											type="submit"
											form="edit-entry-form"
											size="sm"
											tooltip="Save"
										>
											Save
										</TooltipButton>
									)}
								</>
							)}
						</div>
					</div>
				}
			>
				{entry && (
					<EntryForm
						ref={formRef}
						formId="edit-entry-form"
						key={entry.id}
						defaultValues={createEntryFormDefaults(entry)}
						selectedMatter={selectedMatterFromEntry(entry)}
						timekeeperId={entry.timekeeperId}
						documentName={entry.documentName ?? undefined}
						banners={
							<EditEntryBanners
								entry={entry}
								failedExports={failedExports}
								onResolveFeedback={onResolveFeedback}
							/>
						}
						onSubmit={(values) => onSubmit(entry, values, selectedStatus)}
						onExport={(values) => onExport(entry, values)}
						onDuplicate={
							onDuplicate ? (values) => onDuplicate(entry, values) : undefined
						}
						validateOnMount
						readOnly={fieldsReadOnly}
					/>
				)}
				{entry && canUploadFiles && (
					<div className="px-4 pb-4">
						<AttachmentField
							key={entry.id}
							entryId={entry.id}
							attachments={entry.attachments}
							timekeeperId={entry.timekeeperId}
							disabled={isDeleted || isExported}
						/>
					</div>
				)}
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	);
}

interface EditEntryBannersProps {
	entry: Entry;
	failedExports: ReadonlyMap<string, string>;
	onResolveFeedback: (entryId: string) => void;
}

interface FeedbackResolveAction {
	label: string;
	onClick: () => void;
}

function EditEntryBanners({
	entry,
	failedExports,
	onResolveFeedback,
}: EditEntryBannersProps) {
	const feedbackResolveAction: FeedbackResolveAction = {
		label: "Resolve",
		onClick: () => onResolveFeedback(entry.id),
	};

	return (
		<>
			{isTysonMendesProLawCutoffLocked(entry) && (
				<Banner variant="info" title="Entry locked">
					{PROLAW_CUTOFF_LOCK_MESSAGE}
				</Banner>
			)}
			{failedExports.has(entry.id) && (
				<Banner variant="error" title="Export Failed">
					{failedExports.get(entry.id)}
				</Banner>
			)}
			{entry.attorney_feedback && (
				<Banner
					variant="warning"
					title="Admin Feedback"
					action={feedbackResolveAction}
				>
					{entry.attorney_feedback}
				</Banner>
			)}
		</>
	);
}
